import FacebookIcon from "@mui/icons-material/Facebook";
import GoogleIcon from "@mui/icons-material/Google";
import LanguageIcon from "@mui/icons-material/Language";
import InstagramIcon from "@mui/icons-material/Instagram";

export const sourceIcon = (source) => {
  const s = (source || "").toLowerCase();
  if (s.includes("facebook")) return <FacebookIcon fontSize="small" />;
  if (s.includes("google")) return <GoogleIcon fontSize="small" />;
  if (s.includes("instagram")) return <InstagramIcon fontSize="small" />;
  if (s.includes("website")) return <LanguageIcon fontSize="small" />;
  return <LanguageIcon fontSize="small" />;
};

export const initials = (name) => {
  const n = (name || "").trim();
  if (!n) return "L";
  const parts = n.split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase()).join("");
};

export const fmtDateParts = (d) => {
  if (!d) return null;
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return null;
  const m = date.toLocaleString("en-US", { month: "short" });
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  const t = date
    .toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })
    .replace(" ", "");
  return { top: `${m} ${day},`, mid: `${year}`, bot: t };
};

export const pick = (obj, paths, fallback = "") => {
  for (const p of paths) {
    let cur = obj;
    let ok = true;
    for (const part of p.split(".")) {
      if (cur && Object.prototype.hasOwnProperty.call(cur, part))
        cur = cur[part];
      else {
        ok = false;
        break;
      }
    }
    if (ok && cur !== undefined && cur !== null && String(cur).trim() !== "")
      return cur;
  }
  return fallback;
};

const normalizeKey = (s) => String(s || "").trim().toLowerCase().replace(/[\s_]+/g, "_");

const extractMetaRawName = (l) => {
  const raw = l?.custom_fields?.meta_raw_field_data;
  if (!Array.isArray(raw)) return "";
  for (const key of ["full_name", "name"]) {
    const f = raw.find((item) => normalizeKey(item?.name) === key);
    if (f?.values?.[0]) return String(f.values[0]).trim();
  }
  const first = raw.find((item) => normalizeKey(item?.name) === "first_name")?.values?.[0] || "";
  const last = raw.find((item) => normalizeKey(item?.name) === "last_name")?.values?.[0] || "";
  return `${first} ${last}`.trim();
};

export const normalizeLead = (l) => {
  const rawServices = Array.isArray(l?.services)
    ? l.services.filter(Boolean)
    : l?.services
      ? [l.services]
      : [];
  const cfServices = Array.isArray(l?.custom_fields?.services_interested)
    ? l.custom_fields.services_interested.filter(Boolean)
    : [];
  const servicesArr = [...new Set([...rawServices, ...cfServices])];

  const lastCallOutcome =
    Array.isArray(l?.call_logs) && l.call_logs.length > 0
      ? l.call_logs[l.call_logs.length - 1].outcome
      : "Pending";

  const ae = l?.assign_employee;

  const telecallerId =
    (ae && typeof ae === "object"
      ? ae._id || ae.id || ae.userId || ae.value
      : ae) || "";

  const telecallerName =
    ae && typeof ae === "object" ? ae.name || ae.label || ae.full_name : "";

  const telecallerTeam =
    ae && typeof ae === "object" ? ae.team || ae.team_name : "";

  // Get latest 4 calls sorted in descending order (most recent first)
  const callLogs = Array.isArray(l?.call_logs) ? l.call_logs : [];
  const sortedCallLogs = [...callLogs]
    .filter((log) => log && log.call_time)
    .sort((a, b) => new Date(b.call_time) - new Date(a.call_time));
  const followUps = sortedCallLogs.slice(0, 4);

  // Resolve display name: check custom_fields keys (handles "full_name" or "full name"),
  // then extract from meta_raw_field_data, then fall back to client_name
  const cf = l?.custom_fields || {};
  let directName = "";
  for (const [k, v] of Object.entries(cf)) {
    if (normalizeKey(k) === "full_name" && v && typeof v === "string" && v.trim()) {
      directName = v.trim();
      break;
    }
  }
  const metaRawName = extractMetaRawName(l);
  const fallbackName = pick(l, ["client_name", "name", "full_name"], "");
  const displayName = directName
    || metaRawName
    || (fallbackName !== "Meta Lead" ? fallbackName : "")
    || fallbackName;

  return {
    _raw: l,
    _id: l?._id,
    name: displayName || "Unnamed Lead",
    phone: pick(l, ["contact", "phone", "phone_number"], ""),
    email: pick(l, ["email", "contact.email"], ""),
    location: l?.custom_fields?.city,
    priority: l?.priority,
    source:
      pick(l, ["source", "custom_fields.lead_source", "lead_source"], "") ||
      "Unknown",

    services: servicesArr,
    treatmentType: servicesArr.length ? servicesArr.join(", ") : "—",

    telecallerId,
    telecaller: telecallerName || "",
    telecallerTeam,

    callStatus: lastCallOutcome,
    responseLabel: l?.current_status?.key || "",
    createdAt: l?.createdAt || l?.created_at || "",
    current_status: l?.current_status || null,
    followUps, // Latest 4 calls sorted desc: [0] = most recent → Follow-up 4, [3] = oldest → Follow-up 1
  };
};
